/**
 *  @class
 *  @function StickyScroller - 实现筛选器与产品列表的同步滚动
 */
if (!customElements.get('sticky-scroller')) {
  class StickyScroller extends HTMLElement {
    constructor() {
      super();
      // 初始化所有属性
      this.headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
      this.parent = null;
      this.element = null;
      this.productList = null;
      this.observer = null;
      this.newScrollPosition = 0;
      this.oldScrollPosition = 0;
      this.ticking = false;

      // 滚动状态管理，防止事件冲突
      this.isHandlingInternalScroll = false;
      this.isHandlingWindowScroll = false;

      // 绑定事件处理函数
      this.onWindowScroll = this.onWindowScroll.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.onFilterScroll = this.onFilterScroll.bind(this);
      this.onProductScroll = this.onProductScroll.bind(this);
    }

    connectedCallback() {
      // 获取DOM元素
      this.element = this.querySelector('.sticky-scroller--element');
      this.parent = this.parentElement;

      if (this.element && this.parent) {
        // 配置筛选器滚动容器
        this.element.style.overflowY = 'auto';
        this.element.style.maxHeight = `calc(100vh - ${this.headerHeight}px)`;
        this.element.style.scrollBehavior = 'smooth';

        // 获取产品列表元素
        this.productList = this.parent.querySelector('.products.collection');

        if (this.productList) {
          // 添加滚动事件监听器
          this.element.addEventListener('scroll', this.onFilterScroll, { passive: true });
          this.productList.addEventListener('scroll', this.onProductScroll, { passive: true });

          // 配置ResizeObserver以处理DOM变化
          this.observer = new ResizeObserver(() => {
            this.onWindowScroll();
          });
          this.observer.observe(this.parent);
        }
      }

      // 添加窗口滚动事件监听器
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    }

    disconnectedCallback() {
      // 清理事件监听器
      if (this.element) {
        this.element.removeEventListener('scroll', this.onFilterScroll);
      }

      if (this.productList) {
        this.productList.removeEventListener('scroll', this.onProductScroll);
      }

      // 断开ResizeObserver连接
      if (this.observer) {
        this.observer.disconnect();
      }

      // 移除窗口滚动事件监听器
      window.removeEventListener('scroll', this.onWindowScroll);

      // 清空引用
      this.element = null;
      this.parent = null;
      this.productList = null;
      this.observer = null;
    }

    // 处理窗口滚动事件
    onWindowScroll() {
      this.newScrollPosition = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;

      if (!this.ticking && !this.isHandlingInternalScroll) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          this.ticking = false;
          this.oldScrollPosition = this.newScrollPosition;
        });
        this.ticking = true;
      }
    }

    // 处理窗口滚动的同步逻辑
    handleScroll() {
      if (!this.element || !this.productList) return;

      const distance = this.newScrollPosition - this.oldScrollPosition;

      // 仅在粘性定位期间同步滚动
      if (this.parent) {
        const parentRect = this.parent.getBoundingClientRect();

        // 检查是否在粘性定位区域内
        const isSticky = parentRect.top <= this.headerHeight && parentRect.bottom >= 0;

        if (isSticky) {
          this.isHandlingWindowScroll = true;

          // 同步更新筛选器和产品列表的滚动位置
          if (typeof this.element.scrollTop === 'number') {
            this.element.scrollTop += distance;
          }

          if (typeof this.productList.scrollTop === 'number') {
            this.productList.scrollTop += distance;
          }

          // 延迟重置状态，防止事件冲突
          setTimeout(() => { this.isHandlingWindowScroll = false; }, 50);
        }
      }
    }

    // 处理筛选器内部滚动事件
    onFilterScroll() {
      if (!this.element || !this.productList || this.isHandlingWindowScroll) return;

      // 确保所有必要的属性都存在且是数字类型
      if (typeof this.element.scrollTop !== 'number' ||
        typeof this.element.scrollHeight !== 'number' ||
        typeof this.element.clientHeight !== 'number' ||
        typeof this.productList.scrollTop !== 'number' ||
        typeof this.productList.scrollHeight !== 'number' ||
        typeof this.productList.clientHeight !== 'number') {
        return;
      }

      this.isHandlingInternalScroll = true;

      // 计算滚动比例
      const filterScrollableHeight = this.element.scrollHeight - this.element.clientHeight;
      const productScrollableHeight = this.productList.scrollHeight - this.productList.clientHeight;

      // 避免除以零错误
      if (filterScrollableHeight <= 0 || productScrollableHeight <= 0) {
        this.isHandlingInternalScroll = false;
        return;
      }

      // 计算同步滚动位置
      const scrollRatio = this.element.scrollTop / filterScrollableHeight;
      const targetScrollTop = Math.round(scrollRatio * productScrollableHeight);

      // 仅当滚动位置有明显变化时才更新，避免抖动
      if (Math.abs(this.productList.scrollTop - targetScrollTop) > 1) {
        this.productList.scrollTop = targetScrollTop;
      }

      setTimeout(() => { this.isHandlingInternalScroll = false; }, 50);
    }

    // 处理产品列表滚动事件
    onProductScroll() {
      if (!this.element || !this.productList || this.isHandlingWindowScroll) return;

      // 确保所有必要的属性都存在且是数字类型
      if (typeof this.element.scrollTop !== 'number' ||
        typeof this.element.scrollHeight !== 'number' ||
        typeof this.element.clientHeight !== 'number' ||
        typeof this.productList.scrollTop !== 'number' ||
        typeof this.productList.scrollHeight !== 'number' ||
        typeof this.productList.clientHeight !== 'number') {
        return;
      }

      this.isHandlingInternalScroll = true;

      // 计算滚动比例
      const productScrollableHeight = this.productList.scrollHeight - this.productList.clientHeight;
      const filterScrollableHeight = this.element.scrollHeight - this.element.clientHeight;

      // 避免除以零错误
      if (productScrollableHeight <= 0 || filterScrollableHeight <= 0) {
        this.isHandlingInternalScroll = false;
        return;
      }

      // 计算同步滚动位置
      const scrollRatio = this.productList.scrollTop / productScrollableHeight;
      const targetScrollTop = Math.round(scrollRatio * filterScrollableHeight);

      // 仅当滚动位置有明显变化时才更新，避免抖动
      if (Math.abs(this.element.scrollTop - targetScrollTop) > 1) {
        this.element.scrollTop = targetScrollTop;
      }

      setTimeout(() => { this.isHandlingInternalScroll = false; }, 50);
    }
  }

  customElements.define('sticky-scroller', StickyScroller);
}